import React, { useMemo } from 'react';
import { Box, Stack, HStack, Tooltip, Icon, Text } from '@chakra-ui/core';
import { IoMdCheckmark } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { MdRadioButtonChecked, MdOutlineRadioButtonUnchecked } from 'react-icons/md';
import { QuestionType, QuestionOnReviewT } from './ReviewQuestion.types';

const getAnswerConfig = (isCorrect: boolean, isIncorrect: boolean, isUnselectedCorrect: boolean, t: (key: string) => string) => {
  if (isCorrect) {
    return {
      icon: IoMdCheckmark,
      tooltip: t('toReviewQuestions.message.selectedCorrectAnswer'),
    };
  }
  if (isIncorrect) {
    return {
      icon: IoClose,
      tooltip: t('toReviewQuestions.message.selectedIncorrectAnswer'),
    };
  }
  if (isUnselectedCorrect) {
    return {
      icon: IoMdCheckmark,
      tooltip: t('toReviewQuestions.message.correctAnswer'),
    };
  }
  return null;
};

const AnswerOption = React.memo<{
  answer: string;
  isSelected: boolean;
  isPreferred: boolean;
  t: (key: string) => string;
}>(function AnswerOption({ answer, isSelected, isPreferred, t }) {
  const isCorrect = isSelected && isPreferred;
  const isIncorrect = isSelected && !isPreferred;
  const isUnselectedCorrect = !isSelected && isPreferred;

  const answerConfig = getAnswerConfig(isCorrect, isIncorrect, isUnselectedCorrect, t);

  return (
    <HStack flexGrow={1}>
      <Icon
        as={isSelected ? MdRadioButtonChecked : MdOutlineRadioButtonUnchecked}
        fontSize="sm"
        color={isSelected ? 'blue' : undefined}
        opacity={isSelected ? 1 : 0.5}
        data-testid={isSelected ? 'radio-button-checked' : 'radio-button-unchecked'}
      />
      <Text fontSize="sm">{answer}</Text>

      {answerConfig?.icon && (
        <Tooltip label={answerConfig.tooltip} hasArrow bg="black" size="sm" borderRadius="md" fontSize="xs" fontWeight="bold">
          <Box data-testid={answerConfig.icon === IoMdCheckmark ? 'status-icon-checkmark' : 'status-icon-cross'}>
            <Icon as={answerConfig.icon} fontSize="xs" />
          </Box>
        </Tooltip>
      )}
    </HStack>
  );
});

export const ReviewYesNo = React.memo<{ question: QuestionOnReviewT }>(function ReviewYesNo({ question }) {
  const { t } = useTranslation();

  const answerOptions = useMemo(
    () => [
      { label: t('common:expectedQuestions:yes'), isYes: true },
      { label: t('common:expectedQuestions:no'), isYes: false },
    ],
    [t],
  );

  if (question.type !== QuestionType.YES_NO) return null;

  const userAnswer = Boolean(question.answer);

  return (
    <Box>
      <Stack spacing={2} direction="column">
        {answerOptions.map(({ label, isYes }) => (
          <AnswerOption
            key={isYes ? 'yes' : 'no'}
            answer={label}
            isSelected={userAnswer === isYes}
            isPreferred={question.preferredAnswer === isYes}
            t={t}
          />
        ))}
      </Stack>
    </Box>
  );
});
